import StreamBitrateTooltip from "./StreamBitrateTooltip";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useBitrateClock } from "../hooks/useBitrateClock";
import { buildBitrateChart, fetchBitrateHistory, formatBitrateTime, normalizeBitrateSamples, PLAYER_BITRATE_WINDOW_MS, type BitrateClockAnchor } from "../utils/bitrateHistory";

type StreamBitrateChartProps = { streamId: string };
type StreamBitratesResponse = BitrateClockAnchor & { samples?: unknown };

const StreamBitrateChart = ({ streamId }: StreamBitrateChartProps) => {
  const { authorizedFetch } = useAuth();
  const theme = useTheme();
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const bitrateQuery = useQuery<StreamBitratesResponse>({
    queryKey: ["stream-timestamped-bitrates", streamId],
    enabled: Boolean(streamId),
    refetchInterval: 1000,
    refetchIntervalInBackground: true,
    refetchOnMount: "always",
    queryFn: ({ signal }) => fetchBitrateHistory<StreamBitratesResponse>(
      authorizedFetch, `/api/streams/${encodeURIComponent(streamId)}/bitrates`, signal,
    ),
  });
  const now = useBitrateClock(bitrateQuery.data);
  const samples = useMemo(() => normalizeBitrateSamples(bitrateQuery.data?.samples), [bitrateQuery.data]);
  const chart = useMemo(() => buildBitrateChart(samples, now, PLAYER_BITRATE_WINDOW_MS), [samples, now]);
  const maxBitrate = Math.max(1, ...chart.points.map((point) => point.bitrate ?? 0));

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [streamId]);

  const isCompact = containerSize.width < 260 || containerSize.height < 80;
  if (!streamId) return null;

  return (
    <Box ref={containerRef} sx={{
      width: "100%", height: "100%", pt: 1,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
    }}>
      {bitrateQuery.isPending || chart.points.length === 0 ? (
        <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {bitrateQuery.isPending ? <CircularProgress size={24} /> : (
            <Typography variant="body2" color="text.secondary">
              {bitrateQuery.isError ? "Unable to load bitrate history." : "No recent bitrate data."}
            </Typography>
          )}
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chart.points} margin={isCompact
            ? { top: 8, right: 6, left: -20, bottom: 0 }
            : { top: 8, right: 16, left: -20, bottom: 4 }}>
            {!isCompact && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} stroke={theme.palette.divider} />}
            <XAxis hide={isCompact} type="number" dataKey="timestamp" domain={chart.domain}
              allowDataOverflow tickFormatter={formatBitrateTime} tick={{ fontSize: 9, fill: theme.palette.text.secondary }} />
            <YAxis hide={isCompact} domain={[0, maxBitrate]} tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.45} />
                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <RechartsTooltip
              content={({ active, payload }) => <StreamBitrateTooltip active={active} payload={payload} anchorEl={containerRef.current} />}
            />
            <Area type="linear" dataKey="bitrate" connectNulls={false} stroke={theme.palette.primary.main}
              fill={`url(#${gradientId})`} fillOpacity={1} strokeWidth={2} isAnimationActive={false} dot={{ r: 1 }} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default StreamBitrateChart;
